
import i18n from '@/i18n';
import Vue, { PropType } from 'vue';
import {
  Assessment,
  AssessmentResult,
  ASSESSMENT_RESULT_STATUS,
} from '@/models/greathall/assessment.model';
import { Timeline } from '@descomplica/vue-components';
import PostGradFinalTCCRetry from './PostGradFinalTCCRetry.vue';
import PostGradFinalTCCResult from './PostGradFinalTCCResult.vue';
import PostGradFinalTCCSubmission, { FileTCC } from './PostGradFinalTCCSubmission.vue';
import PostGradTCCWaiting from './PostGradTCCWaiting.vue';
import Analytics from '@/helpers/analytics';
import { EVENT_LABELS } from '@/helpers/analytics/labels';
import { RetryDialogAction } from './types';

export default Vue.extend({
  components: {
    Timeline,
    PostGradTCCWaiting,
    PostGradFinalTCCRetry,
    PostGradFinalTCCResult,
    PostGradFinalTCCSubmission,
  },
  props: {
    assessment: {
      type: Object as PropType<Assessment>,
      required: true,
    },
  },
  data() {
    return {
      showToast: false,
      isSending: false,
      hasSendingError: false,
    };
  },
  computed: {
    currentCourseId(): number {
      return this.$store.getters.currentCourse.id;
    },
    assessmentResult(): AssessmentResult {
      return this.assessment.assessmentResultsByUser[0] || {};
    },
    finalTCCWaitingGrading(): boolean {
      return this.assessmentResult.status === ASSESSMENT_RESULT_STATUS.WAITING_GRADING;
    },
    finalTCCWaitingRevaluation(): boolean {
      return this.assessmentResult.status === ASSESSMENT_RESULT_STATUS.WAITING_REVALUATION;
    },
    finalTCCReviewed(): boolean {
      return (
        this.assessmentResult.status === ASSESSMENT_RESULT_STATUS.PASSED ||
        this.assessmentResult.status === ASSESSMENT_RESULT_STATUS.FAILED ||
        this.assessmentResult.status === ASSESSMENT_RESULT_STATUS.WAITING_REVALUATION ||
        this.assessmentResult.status === ASSESSMENT_RESULT_STATUS.FAILED_ELEGIBLE_FOR_REVALUATION
      );
    },
    isAvailableForRetry(): boolean {
      const failedAttempts = this.assessment.assessmentResultsByUser.filter(
        (assessmentResult) => assessmentResult.status === ASSESSMENT_RESULT_STATUS.FAILED,
      ).length;

      return (
        failedAttempts === 1 && this.assessmentResult.status === ASSESSMENT_RESULT_STATUS.FAILED
      );
    },
    lastReplyLink(): string | undefined {
      return this.assessmentResult.lastReply?.link || '#';
    },
  },
  methods: {
    async sendFinalTCC(finalTCCFile: FileTCC, quizFile: FileTCC): Promise<void> {
      if (this.isSending) return;

      this.$emit('update:loading', true);
      this.isSending = true;

      try {
        await this.$store.dispatch('SEND_FINAL_TCC', {
          finalTCCFile: {
            fileName: finalTCCFile.name,
            dataUrl: finalTCCFile.dataUrl,
          },
          quizFile: { fileName: quizFile.name, dataUrl: quizFile.dataUrl },
          assessmentResultId: this.assessmentResult.id,
          dexCourseId: this.currentCourseId,
        });

        this.hasSendingError = false;
      } catch (err) {
        this.hasSendingError = true;
        this.handleToast('sendFinal');
      }

      if (!this.hasSendingError) {
        const data = {
          dexCourseId: this.currentCourseId,
          assessmentId: this.assessment.id,
        };
        await this.$store.dispatch('PULL_ASSESSMENT_TCC_DATA', data);

        this.trackEvent(EVENT_LABELS.tccSendFinal);
      }

      this.isSending = false;
      this.$emit('update:loading', false);
    },
    async requestFinalTCCRevaluation(pleadingText: string): Promise<void> {
      if (this.isSending) return;

      this.$emit('update:loading', true);
      this.isSending = true;

      try {
        await this.$store.dispatch('REQUEST_TCC_REVALUATION', {
          pleadingText,
          dexCourseId: this.currentCourseId,
          assessmentResultId: this.assessmentResult.id,
        });

        this.hasSendingError = false;
      } catch (err) {
        this.hasSendingError = true;
        this.handleToast('requestRevaluation');
      }

      if (!this.hasSendingError) {
        const data = {
          dexCourseId: this.currentCourseId,
          assessmentId: this.assessment.id,
        };
        await this.$store.dispatch('PULL_ASSESSMENT_TCC_DATA', data);

        this.trackEvent(EVENT_LABELS.tccRevaluation);
      }

      this.isSending = false;
      this.$emit('update:loading', false);
    },
    async startTCCRetry(useSameTheme: boolean): Promise<void> {
      this.$emit('update:loading', true);

      try {
        await this.$store.dispatch('START_TCC_RETRY', {
          assessmentId: this.assessment.id,
          dexCourseId: this.currentCourseId,
          useSameTheme,
        });

        await this.$store.dispatch('PULL_ASSESSMENT_TCC_DATA', {
          dexCourseId: this.currentCourseId,
          assessmentId: this.assessment.id,
        });

        await this.$store.dispatch('PULL_MINIMUM_ASSESSMENT_DATA', {
          dexCourseId: this.currentCourseId,
        });

        this.trackEvent(EVENT_LABELS.tccRetry);
        this.$emit('tcc:retry-started');
      } catch (err) {
        this.handleToast('startTCCRetry');
      } finally {
        this.$emit('update:loading', false);
      }
    },
    startRetryDialog({ useSameTheme }: { useSameTheme: boolean }): void {
      const translatePrefix = `yourAssessments.tcc.tabs.finalThesis.retryDialog.${
        useSameTheme ? 'useSameTheme' : 'chooseNewTheme'
      }`;

      this.$emit('startDialog', {
        title: this.$t(`${translatePrefix}.title`),
        description: this.$t(`${translatePrefix}.description`),
        buttons: [
          {
            label: 'Cancelar',
            type: 'outline',
            action: 'cancel',
          },
          {
            label: this.$t(`${translatePrefix}.confirm`),
            type: useSameTheme ? 'success' : 'error',
            action: useSameTheme
              ? RetryDialogAction.USE_SAME_THEME
              : RetryDialogAction.CHOOSE_NEW_THEME,
          },
        ],
        disposeAction: 'cancel',
      });
    },
    finishRetryDialog({ action }: { action: RetryDialogAction }): void {
      if (Object.values(RetryDialogAction).includes(action)) {
        this.startTCCRetry(action === RetryDialogAction.USE_SAME_THEME);
      } else {
        this.$emit('tcc:retry-canceled');
      }
    },
    handleToast(type: string): void {
      const message = i18n.t(`yourAssessments.tcc.tabs.finalThesis.errorMessage.${type}`);
      this.$emit('displayToast', 'error', message);
    },
    trackEvent(eventName: string): void {
      const assessmentResults = this.assessment.assessmentResultsByUser;
      const relatedAssessmentCategory = this.assessment.category.name;

      const attemptNumber =
        assessmentResults.filter((ar) => ar.status === ASSESSMENT_RESULT_STATUS.FAILED).length + 1;

      Analytics.trackEvent(eventName, {
        attemptNumber,
        relatedAssessmentCategory,
      });
    },
  },
});
